import { useEffect, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

type Filter = 'all' | 'unread' | 'critical' | 'info'

const SEVERITY_META: Record<string, { icon: string; classes: string }> = {
  critical: { icon: '🔴', classes: 'border-l-red-500' },
  warning:  { icon: '🟡', classes: 'border-l-amber-500' },
  info:     { icon: '🔵', classes: 'border-l-blue-500' },
}

export function NotificationsView() {
  const qc = useQueryClient()
  const [filter, setFilter] = useState<Filter>('all')
  const [userId, setUserId] = useState<string | null>(null)

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => setUserId(data.user?.id ?? null))
  }, [])

  const { data: notifications } = useQuery({
    queryKey: ['platform-notifications'],
    queryFn: async () => {
      const { data } = await supabase
        .from('platform_notifications')
        .select('*, organizations(name)')
        .order('created_at', { ascending: false })
        .limit(50)
      return data || []
    }
  })

  useEffect(() => {
    const channel = supabase
      .channel('platform-notifications')
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'platform_notifications',
      }, () => qc.invalidateQueries({ queryKey: ['platform-notifications'] }))
      .subscribe()
    return () => { supabase.removeChannel(channel) }
  }, [qc])

  const isRead = (n: any) => userId ? (n.read_by ?? []).includes(userId) : false

  const filtered = (notifications ?? []).filter(n => {
    if (filter === 'unread') return !isRead(n)
    if (filter === 'critical') return n.severity === 'critical'
    if (filter === 'info') return n.severity === 'info'
    return true
  })

  async function markRead(n: any) {
    if (!userId || isRead(n)) return
    await supabase
      .from('platform_notifications')
      .update({ read_by: [...(n.read_by ?? []), userId] })
      .eq('id', n.id)
    qc.invalidateQueries({ queryKey: ['platform-notifications'] })
  }

  async function markAllRead() {
    if (!userId) return
    const unread = (notifications ?? []).filter(n => !isRead(n))
    await Promise.all(unread.map(n =>
      supabase
        .from('platform_notifications')
        .update({ read_by: [...(n.read_by ?? []), userId] })
        .eq('id', n.id)
    ))
    qc.invalidateQueries({ queryKey: ['platform-notifications'] })
  }

  const tabs: { key: Filter; label: string }[] = [
    { key: 'all', label: 'All' },
    { key: 'unread', label: 'Unread' },
    { key: 'critical', label: 'Critical' },
    { key: 'info', label: 'Info' },
  ]

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Notifications</h1>
          <p className="text-justify text-muted-foreground text-sm mt-1">Platform-wide alerts and system messages.</p>
        </div>
        <Button size="sm" variant="outline" onClick={markAllRead}>Mark all read</Button>
      </div>

      <div className="flex gap-1 border-b">
        {tabs.map(tab => (
          <button
            key={tab.key}
            onClick={() => setFilter(tab.key)}
            className={cn(
              'px-3 py-2 text-sm font-medium border-b-2 transition-colors',
              filter === tab.key
                ? 'border-[#C8A84B] text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="space-y-2">
        {filtered.length === 0 ? (
          <p className="text-justify text-sm text-muted-foreground text-center py-12">No notifications.</p>
        ) : filtered.map(n => {
          const meta = SEVERITY_META[n.severity] ?? SEVERITY_META.info
          const read = isRead(n)
          return (
            <div
              key={n.id}
              className={cn(
                'border-l-4 rounded-lg p-3 bg-card border flex items-start justify-between gap-3',
                meta.classes,
                read && 'opacity-60'
              )}
            >
              <div className="flex items-start gap-2 min-w-0">
                <span className="shrink-0">{meta.icon}</span>
                <div className="min-w-0">
                  <p className="text-justify text-sm font-bold">{n.title}</p>
                  <p className="text-sm text-justify text-muted-foreground">{n.message}</p>
                  <p className="text-justify text-[11px] text-muted-foreground mt-1">
                    {new Date(n.created_at).toLocaleString()}
                    {n.organizations?.name && ` · ${n.organizations.name}`}
                  </p>
                </div>
              </div>
              {!read && (
                <Button size="sm" variant="ghost" className="text-xs shrink-0" onClick={() => markRead(n)}>
                  Mark as read
                </Button>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
